interface CacheEntry {
  v: string;
  n: number;
}

export const CacheStrategy = { SMART: 'SMART', FIFO: 'FIFO' } as const;
export type CacheStrategy = (typeof CacheStrategy)[keyof typeof CacheStrategy];

/** 缓存管理器 */
export class CacheManager {
  private cache = new Map<string, CacheEntry>();
  private maxCacheSize: number;
  private strategy: CacheStrategy;
  private evictionRatio: number;

  constructor(
    maxCacheSize: number = 1000,
    strategy: CacheStrategy = CacheStrategy.SMART,
    evictionRatio: number = 0.1,
  ) {
    if (maxCacheSize <= 0) {
      throw new Error('maxCacheSize 必须大于0');
    }
    if (evictionRatio <= 0 || evictionRatio > 1) {
      throw new Error('evictionRatio 必须在 (0, 1] 范围内');
    }

    this.maxCacheSize = maxCacheSize;
    this.strategy = strategy;
    this.evictionRatio = evictionRatio;
  }

  /**
   * 获取缓存值
   */
  get(key: string) {
    const entry = this.cache.get(key);

    if (entry) {
      if (this.strategy === CacheStrategy.SMART) {
        entry.n++;
      }
    }

    return entry;
  }

  /**
   * 设置缓存值
   */
  set(key: string, value: string) {
    const existingEntry = this.cache.get(key);
    if (existingEntry && existingEntry.v === value) {
      if (this.strategy === CacheStrategy.SMART) {
        existingEntry.n++;
        return;
      }
      this.cache.delete(key);
    }

    const newEntry: CacheEntry = { v: value, n: 1 };

    if (this.cache.size >= this.maxCacheSize) {
      this.evict();
    }

    this.cache.set(key, newEntry);
  }

  /**
   * 执行淘汰策略
   */
  private evict() {
    const toRemoveCount = Math.ceil(this.maxCacheSize * this.evictionRatio);
    if (toRemoveCount <= 0) return;

    if (this.strategy === CacheStrategy.SMART) {
      this.evictSmartOptimized(toRemoveCount);
    } else {
      this.evictFIFOOptimized(toRemoveCount);
    }
  }

  /**
   * 智能淘汰策略
   */
  private evictSmartOptimized(toRemoveCount: number) {
    const entries = Array.from(this.cache.entries());
    if (entries.length === 0) return;

    if (toRemoveCount < entries.length / 10) {
      this.evictByFindingMinimums(entries, toRemoveCount);
    } else {
      this.evictByFullSort(entries, toRemoveCount);
    }
  }

  /**
   * 通过寻找最小值来淘汰（适合少量淘汰）
   */
  private evictByFindingMinimums(entries: [string, CacheEntry][], toRemoveCount: number) {
    const tempEntries = [...entries];
    const toRemoveKeys: string[] = [];

    for (let i = 0; i < toRemoveCount && tempEntries.length > 0; i++) {
      let minIndex = 0;
      for (let j = 1; j < tempEntries.length; j++) {
        if (tempEntries[j][1].n < tempEntries[minIndex][1].n) {
          minIndex = j;
        }
      }

      toRemoveKeys.push(tempEntries[minIndex][0]);
      tempEntries.splice(minIndex, 1);
    }

    toRemoveKeys.forEach((key) => this.cache.delete(key));
  }

  /**
   * 通过完整排序来淘汰（适合大量淘汰）
   */
  private evictByFullSort(entries: [string, CacheEntry][], toRemoveCount: number) {
    entries.sort((a, b) => a[1].n - b[1].n);

    for (let i = 0; i < toRemoveCount && i < entries.length; i++) {
      this.cache.delete(entries[i][0]);
    }
  }

  /**
   * FIFO淘汰策略
   */
  private evictFIFOOptimized(toRemoveCount: number) {
    const keysToRemove: string[] = [];
    const iterator = this.cache.keys();

    for (let i = 0; i < toRemoveCount; i++) {
      const { value, done } = iterator.next();
      if (done) break;
      keysToRemove.push(value);
    }

    keysToRemove.forEach((key) => this.cache.delete(key));
  }

  /**
   * 清空缓存
   */
  clear() {
    this.cache.clear();
  }

  /**
   * 获取缓存大小
   */
  get size(): number {
    return this.cache.size;
  }

  /**
   * 删除指定键的缓存
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * 获取缓存中所有键的列表（按插入顺序）
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }
}
